import { DatePipe } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { Subscription } from "rxjs";
import { environment } from "../../../../environments/environment";
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from "../../../config/hotel-tours-list-page.config";
import { pluralize } from "../../pipes/pluralize/pluralize";
import { EventBusService } from "../../../services/event-bus.service";
import { HotelToursSearchService } from "../../../services/search/hotel-tours-search.service";
import { HotelToursSearchParams } from "../../../types/interfaces/hotel-tour-search";
import { ShortSearchCalendarDataService } from "./services/short-search-calendar-data.service";
import { ShortSearchParamsAnalyticsService } from "./services/short-search-params-analytics.service";

export interface ShortSearchCalendarCell {
  key: string | null;
  day: number | null;
}

export interface ShortSearchCalendarMonth {
  title: string;
  cells: ShortSearchCalendarCell[];
}

export interface ShortSearchDepartureCity {
  id: number;
  name: string;
  nameGenitive: string;
}

@Injectable()
export class ShortSearchParamsFacade implements OnDestroy {
  readonly viewStateChanged$ = new Subject<void>();
  readonly weekDays = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
  readonly monthNames = [
    "январь", "февраль", "март", "апрель", "май", "июнь",
    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"
  ];
  readonly departureCities: ShortSearchDepartureCity[];

  searchParams: HotelToursSearchParams;
  newSearchParams: HotelToursSearchParams;
  adultsCount: number;
  childCount: number;
  dateFrom: string;
  dateTo: string;
  nightsFrom: number;
  nightsTo: number;
  nightsOptionsDays: number[] = Array.from({ length: 14 }, (_, i) => i + 3);
  splitRooms = false;
  isFlexibleDates = false;
  selectedDepartureDate: string;
  activeDesktopDateField: "checkIn" | "checkOut" = "checkIn";
  displayedMonthStart: Date;
  availableDateKeys = new Set<string>();
  hasLoadedAvailableDates = false;
  isLoadingAvailableDates = false;
  pendingNightRangeStartDay: number | null = null;
  hoveredNightDay: number | null = null;
  isMobileCalendarSelectionLocked = false;

  private subscriptions: Subscription[] = [];
  private availableDatesSubscription?: Subscription;
  private readonly datePipe = new DatePipe("ru");

  constructor(
    private searchService: HotelToursSearchService,
    private eventBusService: EventBusService,
    private calendarDataService: ShortSearchCalendarDataService,
    private analyticsService: ShortSearchParamsAnalyticsService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
  ) {
    this.departureCities = this.getDepartureCities();
  }

  init() {
    this.searchParams = this.searchService.getLastSearchParams();
    this.newSearchParams = this.searchParams;
    this.adultsCount = this.searchParams.adults;
    this.childCount = this.searchParams.childAges?.length ?? 0;
    this.dateFrom = this.searchParams.dateFrom;
    this.dateTo = this.searchParams.dateTo;
    this.nightsFrom = this.searchParams.nightsFrom;
    this.nightsTo = this.searchParams.nightsTo;
    this.splitRooms = this.splitRoomsDetector();
    this.syncSelectedDateFromSearchParams();

    if (!environment.mobile) {
      this.availableDateKeys = new Set<string>();
      this.hasLoadedAvailableDates = false;
    }

    this.subscriptions.push(
      this.eventBusService.changeTourists$.subscribe((tourists) => {
        this.searchParams.adults = tourists.adult;
        if (tourists.childAges?.length > 0) {
          this.searchParams.childAges = tourists.childAges;
        }
        this.notifyView();
      }),
      this.eventBusService.shortSearchParams$.subscribe((params: HotelToursSearchParams) => {
        this.newSearchParams = Object.assign({}, this.newSearchParams, params);
        this.adultsCount = this.newSearchParams.adults;
        this.childCount = this.newSearchParams.childAges?.length ?? 0;
        this.dateFrom = this.newSearchParams.dateFrom;
        this.dateTo = this.newSearchParams.dateTo ?? this.newSearchParams.dateFrom;
        this.nightsFrom = this.newSearchParams.nightsFrom;
        this.nightsTo = this.newSearchParams.nightsTo;
        this.splitRooms = this.newSearchParams.splitRooms ?? this.splitRoomsDetector();

        if (params.dateFrom || params.dateTo) {
          this.syncSelectedDateFromSearchParams();
        }

        if (environment.mobile && (params.dateFrom || params.dateTo || typeof params.nightsFrom === "number")) {
          this.lockMobileCalendarSelection();
        }

        this.notifyView();
      })
    );

    this.notifyView();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.availableDatesSubscription?.unsubscribe();
    this.viewStateChanged$.complete();
  }

  get calendarMonths(): ShortSearchCalendarMonth[] {
    const firstMonthStart = this.displayedMonthStart || this.getMonthStart(this.getSelectedDateOrFallback());
    const secondMonthStart = new Date(firstMonthStart.getFullYear(), firstMonthStart.getMonth() + 1, 1);

    return [
      this.buildCalendarMonth(firstMonthStart),
      this.buildCalendarMonth(secondMonthStart),
    ];
  }

  get adultsLabel() {
    return this.adultsCount + " " + pluralize(this.adultsCount, ["взрослый", "взрослых", "взрослых"]);
  }

  get childLabel() {
    if (!this.childCount) {
      return "";
    }

    return this.childCount + " " + pluralize(this.childCount, ["ребенок", "детей", "детей"]);
  }

  get datesLabel() {
    const dateFrom = this.datePipe.transform(this.dateFrom, "d MMM")?.replace(".", "");
    const dateTo = this.datePipe.transform(this.dateTo, "d MMM")?.replace(".", "");

    return dateFrom === dateTo ? dateFrom : `${dateFrom} - ${dateTo}`;
  }

  get checkInTitle() {
    return this.isHotelsSearch ? "Дата заселения:" : "Дата вылета:";
  }

  get departureCityLabel() {
    const city = this.departureCities.find((item) => item.id === this.departureCityId);
    return city?.name ?? "Выберите город";
  }

  get hasDepartureCities() {
    return !this.isHotelsSearch && this.departureCities.length > 0;
  }

  get departureCityId() {
    return this.newSearchParams?.departCityId || this.searchParams?.departCityId;
  }

  get checkOutTitle() {
    return "Дата выселения:";
  }

  get checkInLabel() {
    return this.formatDateLabel(this.dateFrom);
  }

  get checkInPillLabel() {
    const baseDate = !this.isHotelsSearch
      ? (this.selectedDepartureDate || this.dateFrom)
      : this.dateFrom;
    const label = this.formatDateLabel(baseDate) || "";

    if (!this.isHotelsSearch && this.isFlexibleDates) {
      return `${label} +-2 дня`;
    }

    return label;
  }

  get checkOutLabel() {
    return this.formatDateLabel(this.dateTo);
  }

  get nightsLabel() {
    const nightsFrom = this.nightsFrom + 1;
    const nightsTo = this.nightsTo + 1;

    if (nightsFrom === nightsTo) {
      return nightsFrom + " " + pluralize(nightsTo, ["день", "дня", "дней"]);
    }

    return nightsFrom + "-" + nightsTo + " " + pluralize(nightsTo, ["день", "дня", "дней"]);
  }

  get nightsDropdownHint() {
    return `Выбраны даты ${this.datesLabel} ± 2 дня на ${this.nightsLabel}`;
  }

  get submitButtonLabel() {
    return this.isDesktopHotelsSearch ? "Найти проживание" : "Найти туры";
  }

  get isHotelsSearch() {
    return this.isDesktopHotelsSearch;
  }

  get desktopSelectedDate() {
    return this.activeDesktopDateField === "checkOut" ? this.dateTo : this.selectedDepartureDate;
  }

  get minCheckInDateKey() {
    return null;
  }

  get maxCheckInDateKey() {
    return null;
  }

  get minCheckOutDateKey() {
    if (!this.isHotelsSearch || !this.dateFrom) {
      return null;
    }

    const checkInDate = this.parseDateKey(this.dateFrom);
    return checkInDate ? this.formatDateKey(this.addDays(checkInDate, 1)) : null;
  }

  get maxCheckOutDateKey() {
    return null;
  }

  prevCalendarMonth() {
    const current = this.displayedMonthStart || this.getMonthStart(this.getSelectedDateOrFallback());
    this.displayedMonthStart = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    this.notifyView();
  }

  nextCalendarMonth() {
    const current = this.displayedMonthStart || this.getMonthStart(this.getSelectedDateOrFallback());
    this.displayedMonthStart = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    this.notifyView();
  }

  resetNightRangePreview() {
    this.pendingNightRangeStartDay = null;
    this.hoveredNightDay = null;
    this.notifyView();
  }

  onNightHover(day: number) {
    this.hoveredNightDay = day;
    this.notifyView();
  }

  onNightHoverLeave() {
    this.hoveredNightDay = null;
    this.notifyView();
  }

  selectNights(day: number) {
    const currentFromDay = this.nightsFrom + 1;
    const currentToDay = this.nightsTo + 1;
    const isCurrentSingleDay = currentFromDay === currentToDay;

    if (this.pendingNightRangeStartDay !== null && this.pendingNightRangeStartDay !== day) {
      const rangeFrom = Math.min(this.pendingNightRangeStartDay, day);
      const rangeTo = Math.max(this.pendingNightRangeStartDay, day);
      this.setNightsRange(rangeFrom, rangeTo);
      return true;
    }

    if (this.pendingNightRangeStartDay !== null && this.pendingNightRangeStartDay === day) {
      this.setNightsRange(day, day);
      return true;
    }

    if (isCurrentSingleDay && day === currentFromDay) {
      this.setNightsRange(day, day);
      return true;
    }

    this.pendingNightRangeStartDay = day;
    this.setNightsRange(day, day);
    this.notifyView();
    return false;
  }

  selectDate(key: string | null) {
    if (!this.canSelectDate(key)) {
      return;
    }

    if (this.isHotelsSearch) {
      this.selectHotelDate(key);
      return;
    }

    this.selectedDepartureDate = key;
    this.applySelectedDateRange();
    this.notifyView();
  }

  setFlexibleDates(isFlexibleDates: boolean) {
    this.isFlexibleDates = isFlexibleDates;
    this.applySelectedDateRange();
    this.notifyView();
  }

  isDateAvailable(key: string | null) {
    if (!key || !this.hasLoadedAvailableDates) {
      return false;
    }

    return this.availableDateKeys.has(key);
  }

  canSelectDate(key: string | null) {
    return !!key;
  }

  openDesktopDates(field: "checkIn" | "checkOut" = "checkIn") {
    this.activeDesktopDateField = field;
    this.displayedMonthStart = this.getCalendarDisplayStart();
    if (!environment.mobile) {
      this.loadAvailableDatesForDropdown();
    }
    this.notifyView();
  }

  lockMobileCalendarSelection() {
    this.isMobileCalendarSelectionLocked = true;
    this.availableDatesSubscription?.unsubscribe();
    this.notifyView();
  }

  unlockMobileCalendarSelection() {
    this.isMobileCalendarSelectionLocked = false;
    this.notifyView();
  }

  prepareMobileCalendarOpen(showModal: () => void) {
    this.unlockMobileCalendarSelection();
    showModal();
  }

  trackCalendarOpen(place: string) {
    this.analyticsService.trackCalendarOpen(place);
  }

  trackTouristsOpen() {
    this.analyticsService.trackTouristsOpen();
  }

  trackModalAfterShow(event: string, payload?: Record<string, unknown>) {
    this.analyticsService.trackModalAfterShow(event, payload);
  }

  trackCalendarApply() {
    this.analyticsService.trackCalendarApply();
  }

  trackTouristsApply() {
    this.analyticsService.trackTouristsApply(this.newSearchParams.adults, this.newSearchParams.childAges);
  }

  apply(source: 'form' | 'calendar' = 'form') {
    const applyParams: HotelToursSearchParams = Object.assign({}, this.newSearchParams, {
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      nightsFrom: this.nightsFrom,
      nightsTo: this.nightsTo,
      adults: this.adultsCount,
      childAges: this.newSearchParams?.childAges ?? [],
    });

    this.newSearchParams = applyParams;
    this.searchService.startNewSearchByHotelId(applyParams, source);
    if (!environment.mobile) {
      this.eventBusService.refreshShortSearchCalendar$.next();
    }
    this.notifyView();
  }

  selectDepartureCity(findId: number) {
    if (!findId || this.newSearchParams?.departCityId === findId) {
      return;
    }

    this.newSearchParams = Object.assign({}, this.newSearchParams, {
      departCityId: findId,
    });

    this.notifyView();
  }

  splitRoomsDetector() {
    return this.adultsCount > 3
      || (this.adultsCount > 1 && (this.adultsCount + this.childCount) > 4)
      || (this.adultsCount > 2 && this.childCount > 0);
  }

  private setNightsRange(dayFrom: number, dayTo: number) {
    this.nightsFrom = dayFrom - 1;
    this.nightsTo = dayTo - 1;

    this.eventBusService.shortSearchParams$.next({
      nightsFrom: this.nightsFrom,
      nightsTo: this.nightsTo,
      daysFrom: dayFrom,
      daysTo: dayTo,
      forceDates: 2,
    });
  }

  private applySelectedDateRange() {
    const selected = this.parseDateKey(this.selectedDepartureDate);
    if (!selected) {
      return;
    }

    const fromDate = this.isFlexibleDates ? this.addDays(selected, -2) : selected;
    const toDate = this.isFlexibleDates ? this.addDays(selected, 2) : selected;
    const nextDateFrom = this.formatDateKey(fromDate);
    const nextDateTo = this.formatDateKey(toDate);

    this.dateFrom = nextDateFrom;
    this.dateTo = nextDateTo;
    this.newSearchParams = Object.assign({}, this.newSearchParams, {
      dateFrom: nextDateFrom,
      dateTo: nextDateTo,
      forceDates: 1,
    });

    this.eventBusService.shortSearchParams$.next({
      dateFrom: nextDateFrom,
      dateTo: nextDateTo,
      forceDates: 1,
    });
  }

  private syncSelectedDateFromSearchParams() {
    const from = this.parseDateKey(this.dateFrom);
    const to = this.parseDateKey(this.dateTo ?? this.dateFrom);

    if (!from || !to) {
      return;
    }

    const diffDays = Math.round((to.getTime() - from.getTime()) / 86400000);

    if (diffDays === 4) {
      this.isFlexibleDates = true;
      this.selectedDepartureDate = this.formatDateKey(this.addDays(from, 2));
    } else {
      this.isFlexibleDates = false;
      this.selectedDepartureDate = this.formatDateKey(from);
    }

    this.displayedMonthStart = this.getCalendarDisplayStart();
  }

  private loadAvailableDates() {
    if (this.isDesktopHotelsSearch) {
      this.availableDatesSubscription?.unsubscribe();
      this.availableDateKeys = new Set<string>();
      this.hasLoadedAvailableDates = false;
      this.isLoadingAvailableDates = false;
      this.notifyView();
      return;
    }

    const departCityId = this.newSearchParams?.departCityId || this.searchParams?.departCityId;
    const countryId = this.newSearchParams?.countryId || this.searchParams?.countryId;

    if (!departCityId || !countryId) {
      return;
    }

    this.availableDatesSubscription?.unsubscribe();

    this.isLoadingAvailableDates = true;
    this.availableDatesSubscription = this.calendarDataService
      .getAvailableDateKeys({
        departCityId,
        countryId,
        nightsFrom: this.nightsFrom,
        nightsTo: this.nightsTo,
        selectedHotelId: this.hotelToursPageConfig.hotel?.hotelId,
      })
      .subscribe({
        next: (sortedDates) => {
          this.availableDateKeys = new Set(sortedDates);
          this.hasLoadedAvailableDates = true;
          this.ensureSelectedDateInAvailableDates(sortedDates);
          this.isLoadingAvailableDates = false;
          this.notifyView();
        },
        error: () => {
          this.availableDateKeys = new Set<string>();
          this.hasLoadedAvailableDates = false;
          this.isLoadingAvailableDates = false;
          this.notifyView();
        }
      });
  }

  private loadAvailableDatesForDropdown() {
    const departCityId = this.newSearchParams?.departCityId || this.searchParams?.departCityId;
    const countryId = this.newSearchParams?.countryId || this.searchParams?.countryId;

    if (!departCityId || !countryId) {
      return;
    }

    this.availableDatesSubscription?.unsubscribe();

    this.isLoadingAvailableDates = true;
    this.availableDatesSubscription = this.calendarDataService
      .getAvailableDateKeys({
        departCityId,
        countryId,
        nightsFrom: this.nightsFrom,
        nightsTo: this.nightsTo,
        selectedHotelId: this.hotelToursPageConfig.hotel?.hotelId,
      })
      .subscribe({
        next: (sortedDates) => {
          this.availableDateKeys = new Set(sortedDates);
          this.hasLoadedAvailableDates = true;
          this.isLoadingAvailableDates = false;
          this.notifyView();
        },
        error: () => {
          this.availableDateKeys = new Set<string>();
          this.hasLoadedAvailableDates = false;
          this.isLoadingAvailableDates = false;
          this.notifyView();
        }
      });
  }

  private ensureSelectedDateInAvailableDates(sortedDates: string[]) {
    if (!sortedDates.length) {
      return;
    }

    const currentDate = this.selectedDepartureDate;
    if (!currentDate) {
      this.selectedDepartureDate = sortedDates[0];
      this.displayedMonthStart = this.getCalendarDisplayStart();
      this.applySelectedDateRange();
      this.notifyView();
      return;
    }

    if (this.availableDateKeys.has(currentDate)) {
      return;
    }

    const nextDate = sortedDates.find((date) => date > currentDate);
    let fallbackDate = nextDate;

    if (!fallbackDate) {
      for (let i = sortedDates.length - 1; i >= 0; i--) {
        if (sortedDates[i] < currentDate) {
          fallbackDate = sortedDates[i];
          break;
        }
      }
    }

    fallbackDate = fallbackDate || sortedDates[0];

    if (fallbackDate !== currentDate) {
      this.selectedDepartureDate = fallbackDate;
      this.displayedMonthStart = this.getCalendarDisplayStart();
      this.applySelectedDateRange();
      this.notifyView();
    }
  }

  private get isDesktopHotelsSearch(): boolean {
    return !environment.mobile && !!this.newSearchParams?.hotelsSearch;
  }

  private buildCalendarMonth(monthStart: Date): ShortSearchCalendarMonth {
    const firstDate = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
    const lastDate = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const firstWeekday = (firstDate.getDay() + 6) % 7;
    const cells: ShortSearchCalendarCell[] = [];

    for (let i = 0; i < firstWeekday; i++) {
      cells.push({ key: null, day: null });
    }

    for (let day = 1; day <= lastDate.getDate(); day++) {
      const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
      cells.push({
        key: this.formatDateKey(date),
        day,
      });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ key: null, day: null });
    }

    const monthName = this.monthNames[monthStart.getMonth()];
    const monthTitle = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${monthStart.getFullYear()}`;

    return {
      title: monthTitle,
      cells,
    };
  }

  private getMonthStart(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private getCalendarDisplayStart() {
    const selectedDate = this.getSelectedDateOrFallback();

    if (!this.isFlexibleDates) {
      return this.getMonthStart(selectedDate);
    }

    return this.getMonthStart(this.addDays(selectedDate, -2));
  }

  private getSelectedDateOrFallback() {
    const dateKey = this.activeDesktopDateField === "checkOut" ? this.dateTo : this.selectedDepartureDate;
    return this.parseDateKey(dateKey) || new Date();
  }

  private parseDateKey(value: string) {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private formatDateKey(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private addDays(date: Date, days: number) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private selectHotelDate(key: string) {
    const selectedDate = this.parseDateKey(key);
    if (!selectedDate) {
      return;
    }

    if (this.activeDesktopDateField === "checkOut") {
      const checkInDate = this.parseDateKey(this.dateFrom);
      const normalizedCheckOut = checkInDate && selectedDate.getTime() <= checkInDate.getTime()
        ? this.addDays(checkInDate, 1)
        : selectedDate;

      this.dateTo = this.formatDateKey(normalizedCheckOut);
      this.newSearchParams = Object.assign({}, this.newSearchParams, {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        forceDates: 1,
      });
    } else {
      this.dateFrom = key;
      this.selectedDepartureDate = key;

      const checkOutDate = this.parseDateKey(this.dateTo);
      if (!checkOutDate || checkOutDate.getTime() <= selectedDate.getTime()) {
        this.dateTo = this.formatDateKey(this.addDays(selectedDate, 1));
      }

      this.newSearchParams = Object.assign({}, this.newSearchParams, {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        forceDates: 1,
      });
    }

    this.eventBusService.shortSearchParams$.next({
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      forceDates: 1,
    });
    this.notifyView();
  }

  private formatDateLabel(value: string) {
    return this.datePipe.transform(value, "d MMM")?.replace(".", "");
  }

  private getDepartureCities(): ShortSearchDepartureCity[] {
    const cities = Array.isArray(this.hotelToursPageConfig.directionDepartCities)
      ? this.hotelToursPageConfig.directionDepartCities
      : [];

    return cities
      .map((city: any) => {
        const id = Number(city?.id);
        const name = city?.name ?? "";
        const nameGenitive = city?.nameGenitive ?? "";

        if (!id || !name) {
          return null;
        }

        return {
          id,
          name,
          nameGenitive,
        };
      })
      .filter((city): city is ShortSearchDepartureCity => !!city)
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }

  private getMobileCalendarState() {
    return {
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      nightsFrom: this.nightsFrom,
      nightsTo: this.nightsTo,
      adults: this.adultsCount,
      childAges: this.newSearchParams?.childAges ?? [],
      searchParams: this.searchParams,
      newSearchParams: this.newSearchParams,
    };
  }

  private notifyView() {
    this.viewStateChanged$.next();
  }
}
